import { join } from 'path-browserify'
import { useCallback, useMemo } from 'react'
import type { NavigateFunction } from 'react-router-dom'

import { message } from '@/components'
import { formatGB2Bytes, formatMB2Bytes } from '@/utils/formatter'

import type { IFile } from '../types'
import { FILE_STATUS } from './useUpload'

/**
 * Path utilities hook
 * Provides dirPath, pathList, and getPathUrl utilities
 */
export function usePathUtilities(basePath: string, searchParams: URLSearchParams) {
  const dirPath = useMemo(() => join(searchParams.get('path') || '/', '/'), [searchParams])
  const pathList = useMemo(() => dirPath.split('/').slice(1, -1), [dirPath])
  const getPathUrl = useCallback(
    (path: string) => `${basePath}?path=${encodeURIComponent(join('/', path))}`,
    [basePath],
  )

  return { dirPath, pathList, getPathUrl }
}

/**
 * Filter uploading tasks (exclude SUCCESS and FAILED)
 */
export function useUploadingTasks(
  uploadList: any[],
  filters?: {
    domain?: number
    target?: string
  },
) {
  return useMemo(() => {
    return uploadList.filter(
      (task) =>
        ![FILE_STATUS.SUCCESS, FILE_STATUS.FAILED].includes(task.status) &&
        (filters?.domain === undefined || task.domain === filters.domain) &&
        (filters?.target === undefined || task.target === filters.target),
    )
  }, [uploadList, filters?.domain, filters?.target])
}

/**
 * Create row selection handler
 */
export function createRowSelectionHandler(
  setSelectedRows: React.Dispatch<React.SetStateAction<IFile[]>>,
) {
  return (file: IFile, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, file])
    } else {
      setSelectedRows((prev) => prev.filter((f) => f.id !== file.id))
    }
  }
}

/**
 * Create file click handler
 * Handles directory navigation and file selection
 */
export function createFileClickHandler(
  dirPath: string,
  selectedRows: IFile[],
  getPathUrl: (path: string) => string,
  handleRowSelection: (file: IFile, checked: boolean) => void,
  navigate: NavigateFunction,
) {
  return (file: IFile) => {
    if (file.isDir) {
      navigate(getPathUrl(join(dirPath, file.name)))
    } else {
      const isSelected = selectedRows.some((row) => row.id === file.id)
      handleRowSelection(file, !isSelected)
    }
  }
}

/**
 * Create delete button click handler
 */
export function createDeleteClickHandler(
  setSelectedRows: React.Dispatch<React.SetStateAction<IFile[]>>,
  setDeleteModalOpen: (open: boolean) => void,
) {
  return (row: IFile) => {
    setSelectedRows([row])
    setDeleteModalOpen(true)
  }
}

/**
 * Create rename button click handler
 */
export function createRenameClickHandler(setRenameInEdit: (file: IFile | undefined) => void) {
  return (row: IFile) => {
    setRenameInEdit(row)
  }
}

/**
 * Create move button click handler
 */
export function createMoveClickHandler(setMoveInEdit: (files: IFile[] | undefined) => void) {
  return (row: IFile) => {
    setMoveInEdit([row])
  }
}

/**
 * Create domain change handler
 */
export function createDomainChangeHandler<T extends { quota_gb: number; usage_mb: number }>(
  volumes: any[],
  setCurrentDomain: (domain: number) => void,
  setCurrentDomainVolume: (volume: T | null) => void,
) {
  return (value: number) => {
    setCurrentDomain(value)
    const selectedVolume = volumes.find((volume: any) => volume.domain === value)
    setCurrentDomainVolume(selectedVolume || null)
  }
}

/**
 * Create upload handler with quota check
 * Supports both Platform and Team upload (with optional additionalParams)
 */
export function createUploadHandler(
  currentDomainVolume: { quota_gb: number; usage_mb: number } | null,
  dirPath: string,
  currentDomain: number,
  uploadFile: (file: File, dirPath: string, domain: number, ...args: any[]) => void,
  t: (key: string) => string,
  additionalParams?: any,
) {
  return ({ target }: React.SyntheticEvent) => {
    if (!currentDomainVolume) return
    const targetInput = target as HTMLInputElement

    if (targetInput.files?.length) {
      const fileSize = targetInput.files[0].size
      const quotaBytes = formatGB2Bytes(currentDomainVolume.quota_gb)
      const usedBytes = formatMB2Bytes(currentDomainVolume.usage_mb)
      const remainingBytes = quotaBytes - usedBytes

      if (fileSize > remainingBytes) {
        message.error(t('common.errorCodes.fs.17'))
        return
      }

      if (additionalParams) {
        uploadFile(targetInput.files[0], dirPath, currentDomain, additionalParams)
      } else {
        uploadFile(targetInput.files[0], dirPath, currentDomain)
      }
    }

    targetInput.value = ''
  }
}

/**
 * Create file delete handler
 * Executes deletion and shows result message
 */
export function createFileDeleteHandler(
  selectedRows: IFile[],
  dirPath: string,
  currentDomain: number,
  deleteFiles: (files: IFile[], domain: number) => Promise<IFile[]>,
  fetchFiles: (path?: string, domain?: number) => Promise<boolean>,
  setDeleteModalOpen: (open: boolean) => void,
  setLoading: (loading: boolean) => void,
  t: (key: string) => string,
) {
  return async () => {
    setLoading(true)
    try {
      const failedList = await deleteFiles(selectedRows, currentDomain)

      if (failedList.length > 0) {
        message.error(t('data.deleteFailed'))
      } else {
        message.success(t('common.message.success'))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }

    setDeleteModalOpen(false)
    fetchFiles(dirPath, currentDomain)
  }
}
